<div class="space-y-6">
  <!-- Header -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
    <div>
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">
        {{ 'SUPPLIERS.TITLE' | translate }}
      </h1>
      <p class="text-gray-600 dark:text-gray-400 mt-1">{{ 'SUPPLIERS.SUBTITLE' | translate }}</p>
    </div>
    <p-button icon="pi pi-plus" [label]="'SUPPLIERS.ADD' | translate" (onClick)="openCreate()" />
  </div>

  <!-- Payable summary -->
  <div
    class="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-4"
  >
    <div class="text-sm text-red-700 dark:text-red-300">
      {{ 'SUPPLIERS.TOTAL_PAYABLE' | translate }}
    </div>
    <div class="text-2xl font-bold text-red-700 dark:text-red-300 mt-1">
      @for (p of totalPayableByCurrency(); track p.currency) {
        <div>{{ p.amount | money: p.currency }}</div>
      } @empty {
        <div>—</div>
      }
    </div>
  </div>

  <!-- Filters -->
  <div class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
    <div class="flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        pInputText
        [(ngModel)]="search"
        (keyup.enter)="load()"
        [placeholder]="'SUPPLIERS.SEARCH' | translate"
        class="w-full sm:w-72"
      />
      <div class="flex items-center gap-2">
        <input type="checkbox" id="onlyDebt" [(ngModel)]="onlyWithDebt" (change)="load()" />
        <label for="onlyDebt" class="text-sm text-gray-700 dark:text-gray-300">
          {{ 'SUPPLIERS.ONLY_DEBT' | translate }}
        </label>
      </div>
      <p-button
        icon="pi pi-search"
        [label]="'SUPPLIERS.REFRESH' | translate"
        severity="secondary"
        (onClick)="load()"
      />
    </div>
  </div>

  <!-- Table -->
  <div
    class="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
  >
    <div class="overflow-x-auto">
      <p-table
        [value]="suppliers()"
        [loading]="loading()"
        [paginator]="true"
        [rows]="20"
        [rowsPerPageOptions]="[20, 50, 100]"
        styleClass="p-datatable-gridlines"
        [tableStyle]="{ 'min-width': '48rem' }"
      >
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 3rem">#</th>
            <th>{{ 'SUPPLIERS.TABLE.NAME' | translate }}</th>
            <th>{{ 'SUPPLIERS.TABLE.PHONE' | translate }}</th>
            <th>{{ 'SUPPLIERS.TABLE.PURCHASED' | translate }}</th>
            <th>{{ 'SUPPLIERS.TABLE.BALANCE' | translate }}</th>
            <th style="width: 14rem">{{ 'SUPPLIERS.TABLE.ACTIONS' | translate }}</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-s let-rowIndex="rowIndex">
          <tr [class.opacity-50]="!s.isActive">
            <td class="text-gray-500 dark:text-gray-400 text-sm">{{ rowIndex + 1 }}</td>
            <td>
              <button
                class="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                (click)="openDetail(s)"
              >
                {{ s.name }}
              </button>
              @if (!s.isActive) {
                <span class="ml-2 text-xs text-gray-400"
                  >({{ 'SUPPLIERS.INACTIVE' | translate }})</span
                >
              }
            </td>
            <td class="text-sm text-gray-600 dark:text-gray-400">{{ s.phoneNumber || '—' }}</td>
            <td class="text-gray-600 dark:text-gray-400">
              @for (b of s.balanceByCurrency; track b.currency) {
                <span class="block">{{ b.totalPurchased | money: b.currency }}</span>
              } @empty {
                <span class="block">—</span>
              }
            </td>
            <td>
              @if (s.balance > 0) {
                <span class="font-bold text-red-600 dark:text-red-400">
                  @for (b of s.balanceByCurrency; track b.currency) {
                    @if (b.balance > 0) {
                      <span class="block">{{ b.balance | money: b.currency }}</span>
                    }
                  }
                </span>
              } @else {
                <span class="text-green-600 dark:text-green-400">{{
                  'SUPPLIERS.NO_DEBT' | translate
                }}</span>
              }
            </td>
            <td>
              <div class="flex gap-1">
                <p-button
                  icon="pi pi-eye"
                  severity="secondary"
                  [text]="true"
                  size="small"
                  (onClick)="openDetail(s)"
                />
                @if (s.balance > 0) {
                  <p-button
                    icon="pi pi-wallet"
                    [label]="'SUPPLIERS.PAY' | translate"
                    severity="success"
                    size="small"
                    (onClick)="openPay(s)"
                  />
                }
                <p-button
                  icon="pi pi-pencil"
                  severity="secondary"
                  [text]="true"
                  size="small"
                  (onClick)="openEdit(s)"
                />
                <p-button
                  icon="pi pi-trash"
                  severity="danger"
                  [text]="true"
                  size="small"
                  (onClick)="deleteSupplier(s)"
                />
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6" class="text-center py-12 text-gray-500 dark:text-gray-400">
              <div class="flex flex-col items-center gap-2">
                <i class="pi pi-truck text-4xl text-gray-300"></i>
                <span>{{ 'SUPPLIERS.EMPTY' | translate }}</span>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>
</div>

<!-- Create/Edit Dialog -->
<p-dialog
  [header]="(editId ? 'SUPPLIERS.EDIT_TITLE' : 'SUPPLIERS.ADD') | translate"
  [(visible)]="editDialogVisible"
  [modal]="true"
  [style]="{ width: 'min(30rem, 95vw)' }"
  [draggable]="false"
  [resizable]="false"
>
  <div class="flex flex-col gap-4 pt-2">
    <div class="flex flex-col gap-1">
      <label for="sup-name" class="text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ 'SUPPLIERS.FORM.NAME' | translate }} <span class="text-red-500">*</span>
      </label>
      <input id="sup-name" type="text" pInputText [(ngModel)]="form.name" class="w-full" />
    </div>
    <div class="flex flex-col gap-1">
      <label for="sup-phone" class="text-sm font-medium text-gray-700 dark:text-gray-300">{{
        'SUPPLIERS.FORM.PHONE' | translate
      }}</label>
      <input id="sup-phone" type="text" pInputText [(ngModel)]="form.phoneNumber" class="w-full" />
    </div>
    <div class="flex flex-col gap-1">
      <label for="sup-address" class="text-sm font-medium text-gray-700 dark:text-gray-300">{{
        'SUPPLIERS.FORM.ADDRESS' | translate
      }}</label>
      <input id="sup-address" type="text" pInputText [(ngModel)]="form.address" class="w-full" />
    </div>
    <div class="flex flex-col gap-1">
      <label for="sup-notes" class="text-sm font-medium text-gray-700 dark:text-gray-300">{{
        'SUPPLIERS.FORM.NOTES' | translate
      }}</label>
      <input id="sup-notes" type="text" pInputText [(ngModel)]="form.notes" class="w-full" />
    </div>
    @if (editId) {
      <div class="flex items-center gap-2">
        <input type="checkbox" id="sup-active" [(ngModel)]="form.isActive" />
        <label for="sup-active" class="text-sm text-gray-700 dark:text-gray-300">{{
          'SUPPLIERS.FORM.ACTIVE' | translate
        }}</label>
      </div>
    }
  </div>
  <ng-template pTemplate="footer">
    <p-button
      [label]="'COMMON.CANCEL' | translate"
      severity="secondary"
      [outlined]="true"
      (onClick)="editDialogVisible = false"
      [disabled]="editSaving"
    />
    <p-button
      [label]="'COMMON.SAVE' | translate"
      icon="pi pi-check"
      (onClick)="saveSupplier()"
      [loading]="editSaving"
    />
  </ng-template>
</p-dialog>

<!-- Detail Dialog -->
<p-dialog
  [header]="'SUPPLIERS.DETAIL_TITLE' | translate"
  [(visible)]="detailDialogVisible"
  [modal]="true"
  [style]="{ width: 'min(48rem, 95vw)' }"
  [draggable]="false"
  [resizable]="false"
>
  @if (detailLoading) {
    <div class="py-10 text-center text-gray-500">
      <i class="pi pi-spin pi-spinner text-2xl"></i>
    </div>
  } @else if (detail()) {
    <div class="flex flex-col gap-4 pt-2">
      <!--
        Hisob HAR valyutada alohida (I11): `$700` kirim va `1 260 000 so'm` to'lovni
        ayirib bo'lmaydi. Har valyuta o'z qatorida turadi.
      -->
      @for (b of detail()!.balanceByCurrency; track b.currency) {
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div class="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
            <div class="text-xs text-gray-500">
              {{ 'SUPPLIERS.DETAIL.PURCHASED' | translate }} · {{ b.currency }}
            </div>
            <div class="font-bold mt-1">{{ b.totalPurchased | money: b.currency }}</div>
          </div>
          <div class="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
            <div class="text-xs text-gray-500">{{ 'SUPPLIERS.DETAIL.PAID' | translate }}</div>
            <div class="font-bold mt-1 text-green-600 dark:text-green-400">
              {{ b.paid | money: b.currency }}
            </div>
          </div>
          <div class="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
            <div class="text-xs text-gray-500">{{ 'SUPPLIERS.DETAIL.BALANCE' | translate }}</div>
            <div
              class="font-bold mt-1"
              [class.text-red-600]="b.balance > 0"
              [class.dark:text-red-400]="b.balance > 0"
              [class.text-gray-500]="b.balance <= 0"
            >
              {{ b.balance | money: b.currency }}
            </div>
          </div>
          @if ($first) {
            <div class="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
              <div class="text-xs text-gray-500">{{ 'SUPPLIERS.FORM.PHONE' | translate }}</div>
              <div class="font-medium mt-1 text-sm">{{ detail()!.phoneNumber || '—' }}</div>
            </div>
          }
        </div>
      } @empty {
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div class="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
            <div class="text-xs text-gray-500">{{ 'SUPPLIERS.FORM.PHONE' | translate }}</div>
            <div class="font-medium mt-1 text-sm">{{ detail()!.phoneNumber || '—' }}</div>
          </div>
        </div>
      }

      <div>
        <h3 class="font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {{ 'SUPPLIERS.DETAIL.PURCHASES' | translate }}
        </h3>
        <p-table [value]="detail()!.purchases" styleClass="p-datatable-sm p-datatable-gridlines">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'SUPPLIERS.DETAIL.NUMBER' | translate }}</th>
              <th>{{ 'SUPPLIERS.DETAIL.DATE' | translate }}</th>
              <th>{{ 'SUPPLIERS.DETAIL.TOTAL' | translate }}</th>
              <th>{{ 'SUPPLIERS.DETAIL.STATUS' | translate }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-p>
            <tr>
              <td>
                <code class="text-xs">{{ p.purchaseNumber }}</code>
              </td>
              <td class="text-sm">{{ p.purchaseDate | date: 'dd.MM.yyyy' }}</td>
              <td>{{ p.totalAmount | money: p.currency }}</td>
              <td>
                <p-tag
                  [value]="'PURCHASES.STATUS.' + p.status | translate"
                  [severity]="statusSeverity(p.status)"
                />
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="4" class="text-center py-4 text-gray-400">
                {{ 'SUPPLIERS.DETAIL.NO_PURCHASES' | translate }}
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <div>
        <h3 class="font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {{ 'SUPPLIERS.DETAIL.PAYMENTS' | translate }}
        </h3>
        <p-table [value]="detail()!.payments" styleClass="p-datatable-sm p-datatable-gridlines">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'SUPPLIERS.DETAIL.DATE' | translate }}</th>
              <th>{{ 'SUPPLIERS.DETAIL.AMOUNT' | translate }}</th>
              <th>{{ 'SUPPLIERS.DETAIL.METHOD' | translate }}</th>
              <th>{{ 'SUPPLIERS.DETAIL.BY' | translate }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-p>
            <tr>
              <td class="text-sm">{{ p.paidAt | date: 'dd.MM.yyyy HH:mm' }}</td>
              <td class="text-green-600 dark:text-green-400">{{ p.amount | money: p.currency }}</td>
              <td class="text-sm">{{ 'FINANCE.METHOD.' + p.paymentMethod | translate }}</td>
              <td class="text-sm">{{ p.createdByName }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="4" class="text-center py-4 text-gray-400">
                {{ 'SUPPLIERS.DETAIL.NO_PAYMENTS' | translate }}
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>
  }
</p-dialog>

<!-- Payment Dialog -->
<p-dialog
  [header]="'SUPPLIERS.PAY_DIALOG.TITLE' | translate"
  [(visible)]="payDialogVisible"
  [modal]="true"
  [style]="{ width: 'min(28rem, 95vw)' }"
  [draggable]="false"
  [resizable]="false"
>
  <div class="flex flex-col gap-4 pt-2">
    @if (paySupplier) {
      <!-- Qarz HAR valyutada alohida — qaysi biri to'lanayotgani aniq ko'rinsin. -->
      <div class="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 text-sm space-y-1">
        <div class="text-gray-500">{{ paySupplier.name }}</div>
        @for (b of paySupplier.balanceByCurrency; track b.currency) {
          @if (b.balance > 0) {
            <div class="flex justify-between">
              <span class="text-gray-500">{{ b.currency }}</span>
              <span class="font-bold text-red-600 dark:text-red-400">
                {{ b.balance | money: b.currency }}
              </span>
            </div>
          }
        }
      </div>
      @if (currencySvc.hasMultipleCurrencies()) {
        <div class="flex flex-col gap-1">
          <label for="sup-pay-currency" class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >{{ 'SUPPLIERS.PAY_DIALOG.CURRENCY' | translate }}
            <span class="text-red-500">*</span></label
          >
          <p-select
            inputId="sup-pay-currency"
            [options]="currencySvc.currencyOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="payCurrency"
            appendTo="body"
            class="w-full"
          />
        </div>
      }
    }
    <div class="flex flex-col gap-1">
      <label for="sup-pay-amount" class="text-sm font-medium text-gray-700 dark:text-gray-300"
        >{{ 'SUPPLIERS.PAY_DIALOG.AMOUNT' | translate }} <span class="text-red-500">*</span></label
      >
      <p-inputnumber
        inputId="sup-pay-amount"
        [min]="1"
        [(ngModel)]="payAmount"
        [useGrouping]="true"
        mode="decimal"
        class="w-full"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label for="sup-pay-method" class="text-sm font-medium text-gray-700 dark:text-gray-300">{{
        'SUPPLIERS.PAY_DIALOG.METHOD' | translate
      }}</label>
      <p-select
        inputId="sup-pay-method"
        [options]="paymentMethods"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="payMethod"
        class="w-full"
        appendTo="body"
      >
        <ng-template let-opt pTemplate="item">{{ opt.label | translate }}</ng-template>
        <ng-template let-opt pTemplate="selectedItem">{{ opt.label | translate }}</ng-template>
      </p-select>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <p-button
      [label]="'COMMON.CANCEL' | translate"
      severity="secondary"
      [outlined]="true"
      (onClick)="payDialogVisible = false"
      [disabled]="paySaving"
    />
    <p-button
      [label]="'SUPPLIERS.PAY_DIALOG.CONFIRM' | translate"
      icon="pi pi-check"
      severity="success"
      (onClick)="confirmPay()"
      [loading]="paySaving"
    />
  </ng-template>
</p-dialog>

<p-confirmdialog />
